import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import { FastForwardRounded, FastRewindRounded, PauseRounded, PlayArrowRounded } from '@mui/icons-material';
import AssistantPhoto from '@mui/icons-material/AssistantPhoto';
import calculateSpeed from '../../../shared/utils/calculateSpeed';

function VehiclePlayerWidget({
  runRec,
  handleStart,
  handleStop,
  speedRec,
  setSpeedRec,
  coordsState,
  coords,
  setPositionHistory,
  onStopSliderDragging,
  formattedLastDate,
  onSaveMarker,
  hideSpeed,
}) {
  const [sliderValue, setSliderValue] = useState(coordsState.length || coords.length);
  const rafIdRef = useRef(null);
  const latestValueRef = useRef(sliderValue);

  useEffect(() => {
    latestValueRef.current = sliderValue;
  }, [sliderValue]);

  useEffect(() => {
    // если пользователь не тащит слайдер — синхронизируем положение со стейтом
    setSliderValue(coordsState.length || coords.length);
  }, [coordsState.length, coords.length]);

  const schedulePositionUpdate = (value) => {
    latestValueRef.current = value;
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      setPositionHistory(latestValueRef.current);
    });
  };

  return (
    <Box>
      <Typography
        sx={{
          width: 'max-content',
          margin: 'auto',
        }}
        id="non-linear-slider"
        gutterBottom
      >
        {speedRec}x {formattedLastDate}
      </Typography>
      <Box
        sx={{
          width: 'max-content',
          marginLeft: 'auto',
        }}
      >
        <IconButton onClick={() => speedRec !== 1 && setSpeedRec((prev) => prev / 2)} aria-label="previous song">
          <FastRewindRounded fontSize="large" />
        </IconButton>
        <IconButton aria-label="play">
          {runRec ? (
            <PauseRounded onClick={handleStop} sx={{ fontSize: '3rem' }} />
          ) : (
            <PlayArrowRounded onClick={handleStart} sx={{ fontSize: '3rem' }} />
          )}
        </IconButton>
        <IconButton
          onClick={() => speedRec <= 15 && setSpeedRec((prev) => (prev === 1 ? 2 : prev * 2))}
          aria-label="next song"
        >
          <FastForwardRounded fontSize="large" />
        </IconButton>
        <IconButton onClick={onSaveMarker}>
          <AssistantPhoto fontSize="large" />
        </IconButton>
      </Box>

      <Box>
        {!hideSpeed && (
          <Typography id="non-linear-slider" gutterBottom>
            Скорость:{' '}
            {coordsState.length > 1
              ? (() => {
                  const calculatedSpeed = calculateSpeed(
                    coordsState[coordsState.length - 2]?.latitude,
                    coordsState[coordsState.length - 2]?.longitude,
                    coordsState[coordsState.length - 2]?.date || coordsState[coordsState.length - 2]?.dateTime,
                    coordsState[coordsState.length - 1]?.latitude,
                    coordsState[coordsState.length - 1]?.longitude,
                    coordsState[coordsState.length - 1]?.date || coordsState[coordsState.length - 1]?.dateTime,
                  );
                  return calculatedSpeed !== Infinity && calculatedSpeed !== null ? calculatedSpeed : '-';
                })()
              : 0}{' '}
            км/ч.
          </Typography>
        )}
        <Slider
          value={sliderValue}
          min={1}
          step={1}
          max={coords?.length || 0}
          getAriaValueText={() => {}}
          valueLabelFormat={formattedLastDate}
          onChange={(_, value) => {
            setSliderValue(value);
            schedulePositionUpdate(value);
          }}
          onChangeCommitted={(_, value) => {
            onStopSliderDragging?.(value);
          }}
          valueLabelDisplay="auto"
          aria-labelledby="non-linear-slider"
        />
      </Box>
    </Box>
  );
}

VehiclePlayerWidget.propTypes = {
  runRec: PropTypes.bool.isRequired,
  handleStart: PropTypes.func.isRequired,
  handleStop: PropTypes.func.isRequired,
  speedRec: PropTypes.number.isRequired,
  setSpeedRec: PropTypes.func.isRequired,
  coordsState: PropTypes.arrayOf(
    PropTypes.shape({
      longitude: PropTypes.number,
      latitude: PropTypes.number,
      date: PropTypes.string,
      dateTime: PropTypes.string,
    }),
  ),
  coords: PropTypes.arrayOf(
    PropTypes.shape({
      longitude: PropTypes.number,
      latitude: PropTypes.number,
      date: PropTypes.string,
      dateTime: PropTypes.string,
    }),
  ),
  setPositionHistory: PropTypes.func.isRequired,
  onStopSliderDragging: PropTypes.func,
  formattedLastDate: PropTypes.string.isRequired,
  onSaveMarker: PropTypes.func.isRequired,
  hideSpeed: PropTypes.bool,
};

VehiclePlayerWidget.defaultProps = {
  coordsState: [],
  coords: [],
  hideSpeed: false,
  onStopSliderDragging: null,
};

export default VehiclePlayerWidget;
